'use client'

import { useState } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import {
    DEFAULT_PAGE,
    DEFAULT_PER_PAGE,
    PaginationQueryParams,
    addPaginationQueryParamsToPath,
    extractQueryParamsForPagination,
} from './PaginationQueryParamHelper'

interface Props {
    /** Stores the pagination query params in the URL and reads them from the URL */
    bindToURL?: boolean
    defaultPage?: number
    defaultPerPage?: number
}

interface ReturnType extends PaginationQueryParams {
    setPage: (page: number) => void
    setPerPage: (perPage: number) => void
    resetToFirstPage: () => void
}

export const usePaginationQueryParams = ({
    bindToURL = true,
    defaultPage = DEFAULT_PAGE,
    defaultPerPage = DEFAULT_PER_PAGE,
}: Props = {}): ReturnType => {
    const searchParams = useSearchParams()
    const pathname = usePathname()
    const router = useRouter()

    const defaults: PaginationQueryParams = { page: defaultPage, perPage: defaultPerPage }
    const [pagination, setPagination] = useState<PaginationQueryParams>(() =>
        bindToURL ? extractQueryParamsForPagination(searchParams, defaults) : defaults
    )

    const updatePagination = (next: PaginationQueryParams): void => {
        setPagination(next)
        if (!bindToURL) return

        const currentQuery = searchParams.toString()
        const currentUrl = currentQuery ? `${pathname}?${currentQuery}` : pathname
        const nextUrl = addPaginationQueryParamsToPath(currentUrl, next, defaults)
        if (nextUrl === currentUrl) return

        router.replace(nextUrl, { scroll: false })
    }

    return {
        ...pagination,
        setPage: (page: number) => updatePagination({ ...pagination, page }),
        setPerPage: (perPage: number) => updatePagination({ page: defaultPage, perPage }),
        resetToFirstPage: () => updatePagination({ ...pagination, page: defaultPage }),
    }
}
